import { GoogleAnalyticsService } from './google-analytics.service';
import * as localizedFormat from 'dayjs/plugin/localizedFormat';
import { environment } from 'environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { UtilityService } from './utility.service';
import * as timezone from 'dayjs/plugin/timezone';
import { EventEmitter, Injectable } from '@angular/core';
import {
    GoogleLoginProvider,
    SocialAuthService,
    SocialUser,
} from '@abacritt/angularx-social-login';
import { Router } from '@angular/router';
import * as utc from 'dayjs/plugin/utc';
import { Store } from '@ngrx/store';
import * as dayjs from 'dayjs';
import {
    setUserToken_And_LoginTime,
    resetUserToken,
    setFilterValue,
    selectRowData,
    setUserToken,
    getAllUsers,
} from '@app/store/actions';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    verifyPasswordResetCode,
    sendPasswordResetEmail,
    signInWithCredential,
    confirmPasswordReset,
    fetchSignInMethodsForEmail,
    GoogleAuthProvider,
    OAuthProvider,
    signInWithPopup,
    linkWithCredential,
    signOut,
    getAuth,
} from 'firebase/auth';
import 'firebase/auth';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(localizedFormat);

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    resetPrerequisiteError = false;
    unauthorizedProvider = false;
    manyAttemptsError: boolean;
    roleConfigRoute: string;
    isLoadingUsers = false;
    isUserDisabled = false;
    isSocialLogin = false;
    loginLoading = false;
    isLoading = false;
    showAlert = false;
    isAgent = false;
    message = '';
    auth = getAuth();

    userEmitter = new EventEmitter<any>();
    constructor(
        private googleAnalyticService: GoogleAnalyticsService,
        private socialAuth: SocialAuthService,
        private translate: TranslateService,
        private utility: UtilityService,
        private store: Store<any>,
        public router: Router
    ) {
        // 🔑 Associate sign-in with a tenant ID if provided
        if (environment.tenantID) {
            this.auth.tenantId = environment.tenantID;
        }

        this.socialAuth.authState.subscribe((user: SocialUser) => {
            if (!user) {
                return;
            }

            // Only handle Google through library (Microsoft uses Firebase direct)
            if (user.provider === 'GOOGLE') {
                const credential = GoogleAuthProvider.credential(user.idToken);

               signInWithCredential(getAuth(), credential)
                .then((res: any) => {
                    this.userEmitter.emit(res);
                })
                .catch((error) => {
                    console.error('Firebase credential sign-in failed:', error);
                    this.googleAnalyticService.onAgentStatusChange(
                        'failed',
                        user.email
                    );
                    this.loginLoading = false;
                    this.isLoading = false;
                    this.showAlert = true;
                    setTimeout(() => {
                        this.showAlert = false;
                    }, 10000);
                });
            } else {
                console.log('Provider not handled by library:', user.provider);
            }
        });
    }

    async socialSignIn(): Promise<SocialUser | void> {
        this.isUserDisabled = false;
        this.isAgent = false;

        // Google SSO through library
        return await this.socialAuth
            .signIn(GoogleLoginProvider.PROVIDER_ID)
            .catch(() => {
                this.loginLoading = false;
                this.isLoadingUsers = false;
                this.showAlert = true;
                this.isLoading = false;
                setTimeout(() => {
                    this.showAlert = false;
                }, 10000);
            });
    }

    // Microsoft Sign-In using Firebase Direct (signInWithPopup)
    async microsoftSignInWithFirebase(): Promise<void> {
        this.isUserDisabled = false;
        this.isAgent = false;
        this.loginLoading = true;
        this.isLoading = true;

        // Create Microsoft OAuth provider
        const provider = new OAuthProvider('microsoft.com');

        // Configure for multi-tenant (personal + organizational accounts)
        provider.setCustomParameters({
            tenant: 'common',
        });

        // Add required scopes
        provider.addScope('openid');
        provider.addScope('profile');
        provider.addScope('email');
        provider.addScope('User.Read');

        try {
            // Sign in with popup - Firebase handles everything
            const result = await signInWithPopup(this.auth, provider);

            // Process the authenticated user
            this.processAuthenticatedUser(result);

        } catch (error: any) {
            console.error('Microsoft Firebase SSO Error:', error);
            console.error('Error code:', error?.code);
            console.error('Error message:', error?.message);

            // Handle account-exists-with-different-credential error
            if (error?.code === 'auth/account-exists-with-different-credential') {
                await this.handleAccountLinking(error, provider);
                return;
            }

            this.googleAnalyticService.onAgentStatusChange(
                'failed',
                error?.email || 'unknown'
            );

            this.loginLoading = false;
            this.isLoading = false;
            this.showAlert = true;
            this.unauthorizedProvider = false;

            setTimeout(() => {
                this.showAlert = false;
            }, 10000);
        }
    }

    // Handle account linking when user exists with different provider
    private async handleAccountLinking(error: any, pendingProvider: OAuthProvider): Promise<void> {
        try {
            // Extract email from Firebase error object (stored in customData)
            const email = error.customData?.email || error.email;
            const pendingCredential = OAuthProvider.credentialFromError(error);


            if (!email) {
                console.error('No email found in error object:', error);
                throw new Error('Unable to retrieve email from error');
            }

            if (!pendingCredential) {
                console.error('No pending credential found in error object');
                throw new Error('Unable to retrieve credential from error');
            }

            // Get the existing sign-in methods for this email
            const methods = await fetchSignInMethodsForEmail(this.auth, email);


            // Check if account uses Google - we can link it
            if (methods.includes('google.com')) {
                // Ask user if they want to link their Microsoft account with Google
                const linkConfirm = await this.utility.openDialog(
                    'DIALOG_MESSAGES.LINK_MICROSOFT_WITH_GOOGLE',
                    email,
                    ['cancel', 'link']
                );

                if (linkConfirm === 'link') {
                    // User confirmed - now open Google popup (user-initiated, won't be blocked)
                    const googleProvider = new GoogleAuthProvider();
                    const googleResult = await signInWithPopup(this.auth, googleProvider);

                    // Link the Microsoft credential to the Google account
                    await linkWithCredential(googleResult.user, pendingCredential);
                    // Process the authenticated user
                    this.processAuthenticatedUser(googleResult);
                } else {
                    // User cancelled
                    this.loginLoading = false;
                    this.isLoading = false;
                }

            } else if (methods.includes('password')) {
                // Ask user to enter their password to link Microsoft account
                const passwordDialog = await this.utility.openDialog(
                    'DIALOG_MESSAGES.LINK_MICROSOFT_WITH_PASSWORD',
                    email,
                    ['cancel', 'linkPassword', 'link']
                );

                if (passwordDialog && passwordDialog['type'] === 'link' && passwordDialog['password']) {
                    try {
                        // Sign in with email/password first
                        const userCredential = await signInWithEmailAndPassword(
                            this.auth,
                            email,
                            passwordDialog['password']
                        );

                        // Link the Microsoft credential
                        await linkWithCredential(userCredential.user, pendingCredential);


                        // Process the authenticated user
                        this.processAuthenticatedUser(userCredential);

                    } catch (passwordError: any) {
                        console.error('Failed to link with password:', passwordError);
                        if (passwordError.code === 'auth/wrong-password') {
                            this.message = 'Incorrect password. Please try again.';
                        }

                        this.showAlert = true;
                        this.loginLoading = false;
                        this.isLoading = false;

                        setTimeout(() => {
                            this.showAlert = false;
                        }, 10000);
                    }
                } else {
                    // User cancelled
                    this.loginLoading = false;
                    this.isLoading = false;
                }

            } else {
                // Unknown provider - show error
                console.error('Unknown existing sign-in method:', methods);
                this.unauthorizedProvider = true;
                this.showAlert = true;
                this.loginLoading = false;
                this.isLoading = false;

                setTimeout(() => {
                    this.showAlert = false;
                    this.unauthorizedProvider = false;
                }, 15000);
            }

        } catch (linkError: any) {
            console.error('Error during account linking:', linkError);
            const email = error.customData?.email || error.email || 'unknown';
            this.googleAnalyticService.onAgentStatusChange(
                'failed',
                email
            );

            this.loginLoading = false;
            this.isLoading = false;
            this.showAlert = true;
            this.unauthorizedProvider = false;

            setTimeout(() => {
                this.showAlert = false;
            }, 10000);
        }
    }

    // Extracted method to process authenticated user (used by both Microsoft and credential sign-in)
    private processAuthenticatedUser(result: any): void {
        result.user.getIdTokenResult().then((idTokenResult: any) => {
            const currentTime = dayjs();
            const originalTime = currentTime.clone();

            this.googleAnalyticService.onAgentStatusChange('success', result.user.email);

            const userRole = idTokenResult.claims.role
                ? String(idTokenResult.claims.role).toLowerCase()
                : '';

            this.store.dispatch(
                setUserToken({
                    userToken: idTokenResult.token,
                    email: result.user.email,
                    userRole,
                    userLoginTime: originalTime['_d'],
                    providerIDs: result.user.providerData.map(
                        (element: any) => element.providerId
                    ),
                })
            );

            this.roleConfigRoute = '';
            const roleConfig = environment.rolesWithLoginAccess.find(
                (node) => node.role === userRole
            );

            if (roleConfig) {
                if (userRole !== 'admin') {
                    this.loginLoading = false;
                    this.router.navigate([roleConfig.route]);
                }

                if (userRole === 'admin') {
                    this.store.dispatch(getAllUsers());
                    this.utility.isGetAllUsersApiCalled = true;
                }

                this.roleConfigRoute = roleConfig.route;

                this.store.dispatch(
                    selectRowData({
                        node: roleConfig.node.toLowerCase(),
                        rowData: {},
                    })
                );
                this.isLoading = false;

                this.store.dispatch(
                    setFilterValue({
                        node: roleConfig.node,
                        filterValue: '',
                        rowIndex: null,
                        pageNumber: 0,
                    })
                );
            } else {
                this.isLoadingUsers = false;
                this.signOut();
                this.showAlert = true;
                this.isLoading = false;
                this.isAgent = true;
                this.loginLoading = false;
                setTimeout(() => {
                    this.showAlert = false;
                    this.isAgent = false;
                }, 10000);
            }
        });
    }

    async signIn(email: string, password: string): Promise<any> {
        this.isUserDisabled = false;
        this.isAgent = false;

        await signInWithEmailAndPassword(this.auth, email, password)
            .then((result) => {
                result.user
                    .getIdTokenResult()
                    .then((tokenValue: any) => {
                        const currentTime = dayjs();
                        const originalTime = currentTime.clone();
                        this.googleAnalyticService.onAgentStatusChange(
                            'success',
                            email
                        );

                        const userRole = tokenValue.claims.role
                            ? tokenValue.claims.role.toLowerCase()
                            : '';
                        this.store.dispatch(
                            setUserToken({
                                userToken: tokenValue.token,
                                email: result.user.email,
                                userRole,
                                userLoginTime: originalTime['_d'],
                            })
                        );

                        this.roleConfigRoute = '';
                        const roleConfig =
                            environment.rolesWithLoginAccess.find(
                                (node) => node.role === userRole
                            );

                        if (roleConfig) {
                            if (userRole !== 'admin') {
                                this.loginLoading = false;
                                this.router.navigate([roleConfig.route]);
                            }

                            if (userRole === 'admin') {
                                this.store.dispatch(getAllUsers());
                                this.utility.isGetAllUsersApiCalled = true;
                            }

                            this.roleConfigRoute = roleConfig.route;

                            this.store.dispatch(
                                selectRowData({
                                    node: roleConfig.node.toLowerCase(),
                                    rowData: {},
                                })
                            );
                            this.isLoading = false;

                            this.store.dispatch(
                                setFilterValue({
                                    node: roleConfig.node,
                                    filterValue: '',
                                    rowIndex: null,
                                    pageNumber: 0,
                                })
                            );
                        } else {
                            //If the user role doesn't exist in the environment
                            this.isLoadingUsers = false;
                            this.signOut();
                            this.showAlert = true;
                            this.isLoading = false;
                            this.isAgent = true;
                            this.loginLoading = false;
                            setTimeout(() => {
                                this.showAlert = false;
                                this.isAgent = false;
                            }, 10000);
                        }
                    })
                    .catch(() => {
                        this.loginLoading = false;
                        this.googleAnalyticService.onAgentStatusChange(
                            'failed',
                            email
                        );
                        this.isLoadingUsers = false;
                        this.showAlert = true;
                        this.isLoading = false;
                        setTimeout(() => {
                            this.showAlert = false;
                        }, 10000);
                    });
            })
            .catch((error) => {
                this.loginLoading = false;
                switch (error.code) {
                    case 'auth/user-disabled':
                        this.isUserDisabled = true;
                        this.manyAttemptsError = false;
                        break;
                    case 'auth/too-many-requests':
                        this.isUserDisabled = false;
                        this.manyAttemptsError = true;
                        break;
                    default:
                        this.isUserDisabled = false;
                        break;
                }

                this.googleAnalyticService.onAgentStatusChange('failed', email);

                this.isLoadingUsers = false;
                this.showAlert = true;
                this.isLoading = false;
                setTimeout(() => {
                    this.showAlert = false;
                }, 10000);
            });
    }

    signOut(email?: string): void {
        if (email) {
            this.googleAnalyticService.onAgentStatusChange('logout', email);
        }
        this.isLoading = false;
        this.store.dispatch(resetUserToken());

        if (this.isSocialLogin) {
            this.socialAuth.signOut();
        } else {
            signOut(this.auth);
        }

        this.isSocialLogin = false;

        this.router.navigate(['/login']);
    }

    // Sign up with email/password
    signUp(email: string, password: string): any {
        return createUserWithEmailAndPassword(this.auth, email, password);
    }

    resetPassword(email: string): any {
        return sendPasswordResetEmail(this.auth, email).catch((error) => {
            const str = error.status.toString();
            if (str.substring(0, 2) === '40') {
                this.signOut();
            }
        });
    }

    checkToken(): void {
        this.getCurrentUser()
            .then(() => {
                getAuth()
                    ?.currentUser?.getIdTokenResult(true)
                    .then((idTokenResult) => {
                        this.store.dispatch(
                            setUserToken_And_LoginTime({
                                userToken: idTokenResult.token,
                                userLoginTime: idTokenResult.issuedAtTime,
                            })
                        );
                    })
                    .catch(() => {
                        this.signOut();
                    });
            })
            .catch(() => {
                this.signOut();
            });
    }

    credentialSignIn(user: any): void {
        const currentTime = dayjs();
        const originalTime = currentTime.clone();
        this.googleAnalyticService.onAgentStatusChange('success', user.email);

        const userInfo = JSON.parse(user?.reloadUserInfo?.customAttributes);

        const userRole = userInfo ? userInfo.role : '';

        this.store.dispatch(
            setUserToken({
                userToken: user.accessToken,
                email: user.email,
                userRole,
                userLoginTime: originalTime['_d'],
                providerIDs: user.providerData.map(
                    (element) => element.providerId
                ),
            })
        );

        this.roleConfigRoute = '';
        const roleConfig = environment.rolesWithLoginAccess.find(
            (node) => node.role === userRole
        );

        if (roleConfig) {
            if (userRole !== 'admin') {
                this.isLoadingUsers = false;
                this.loginLoading = false;
                this.router.navigate([roleConfig.route]);
            }

            if (userRole === 'admin') {
                this.store.dispatch(getAllUsers());
                this.utility.isGetAllUsersApiCalled = true;
            }

            this.roleConfigRoute = roleConfig.route;

            this.store.dispatch(
                selectRowData({
                    node: roleConfig.node.toLowerCase(),
                    rowData: {},
                })
            );
            this.isLoading = false;

            this.store.dispatch(
                setFilterValue({
                    node: roleConfig.node,
                    filterValue: '',
                    rowIndex: null,
                    pageNumber: 0,
                })
            );
        } else {
            this.googleAnalyticService.onAgentStatusChange(
                'failed',
                user.email
            );
            //If the user role doesn't exist in the environment
            this.isLoadingUsers = false;
            this.signOut();
            this.showAlert = true;
            this.isLoading = false;
            this.loginLoading = false;
            setTimeout(() => {
                this.showAlert = false;
            }, 10000);
        }
    }

    getCurrentUser(): any {
        return new Promise<any>((resolve, reject) => {
            getAuth()?.onAuthStateChanged(function (user) {
                if (user) {
                    resolve(user);
                } else {
                    reject('No user logged in');
                }
            });
        });
    }

    getParameterByName(name: string): any {
        var match = RegExp('[?&]' + name + '=([^&]*)').exec(
            window.location.search
        );
        if (match && decodeURIComponent(match[1].replace(/\+/g, ' '))) {
            return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
        } else {
            return null;
        }
    }

    resetPasswordRequest(newPassword: string): void {
        this.message = '';

        //   // TODO: Implement getParameterByName()

        //   // Get the action to complete.
        var mode = this.getParameterByName('mode');

        // Get the one-time code from the query parameter.
        var actionCode = this.getParameterByName('oobCode');

        // (Optional) Get the continue URL from the query parameter if available.
        var continueUrl = this.getParameterByName('');

        // (Optional) Get the language code if available.
        var lang = this.getParameterByName('lang') || 'en';

        if (mode !== null && actionCode !== null) {
            // Handle the user management action.
            switch (mode) {
                case 'resetPassword':
                    // Display reset password handler and UI.
                    this.handleResetPassword(
                        actionCode,
                        continueUrl,
                        lang,
                        newPassword
                    );
                    break;
                default:
                // Error: invalid mode.
            }
        } else {
            this.resetPrerequisiteError = true;
            this.isLoading = false;
            this.message = this.translate.instant('FORMS.INVALID_RESET_URL');
        }
    }

    handleResetPassword(
        actionCode: any,
        continueUrl: string,
        lang: string,
        newPassword: string
    ): any {
        // Verify the password reset code is valid.

        return verifyPasswordResetCode(this.auth, actionCode)
            .then(() => {
                confirmPasswordReset(this.auth, actionCode, newPassword)
                    .then(() => {
                        this.isLoading = false;
                        // Password reset has been confirmed and new password updated.
                        this.router.navigate(['/login']);
                    })
                    .catch(() => {
                        // Error occurred during confirmation. The code might have expired or the
                        // password is too weak.
                        this.message = this.translate.instant(
                            'FORMS.ERROR_OCCURRED_DURING_CONFIRMATION'
                        );
                        this.isLoading = false;
                        this.resetPrerequisiteError = false;
                        setTimeout(() => {
                            this.showAlert = false;
                        }, 1500);
                    });
            })
            .catch((error) => {
                // Invalid or expired action code. Ask user to try to reset the password
                // again.
                if (error.code === 'auth/network-request-failed') {
                    this.message = this.translate.instant(
                        'FORMS.CONNECTION_ERROR'
                    );
                } else {
                    this.message = this.translate.instant(
                        'FORMS.ACTION_CODE_IS_INVALID'
                    );
                }

                this.resetPrerequisiteError = true;
                this.isLoading = false;
                this.showAlert = false;
            });
    }

   
}
